import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { searchMulti, posterUrl, getTitle, getMediaType, getReleaseYear } from "../services/tmdb";

function NavBar(props) {
  const { currentUser, loginWithGoogle, logout } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  
  const suggestionRef = useRef(null);
  const mobileSuggestionRef = useRef(null);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (query.trim().length > 2) {
        try {
          const data = await searchMulti(query.trim());
          if (data?.results) {
            // Filter to only movies and TV, max 8
            const filtered = data.results
              .filter(r => r.media_type === "movie" || r.media_type === "tv")
              .slice(0, 8);
            setSuggestions(filtered);
            setShowSuggestions(true);
          } else {
            setSuggestions([]);
          }
        } catch (error) {
          console.error("Error fetching suggestions:", error);
        }
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    };

    const debounceTimer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounceTimer);
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        (!suggestionRef.current || !suggestionRef.current.contains(event.target)) &&
        (!mobileSuggestionRef.current || !mobileSuggestionRef.current.contains(event.target))
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    // If we have a first suggestion, navigate to it
    if (suggestions.length > 0) {
      const first = suggestions[0];
      setShowSuggestions(false);
      setShowMobileSearch(false);
      navigate(`/details/${getMediaType(first)}/${first.id}`);
    }
  };

  const renderSearchForm = (isMobile) => (
    <div className={`position-relative ${isMobile ? 'w-100' : ''}`} ref={isMobile ? mobileSuggestionRef : suggestionRef}>
      <form className="d-flex m-0 rounded-pill" onSubmit={handleSearch} style={{ backgroundColor: "rgba(255, 255, 255, 0.1)", border: "1px solid rgba(255, 255, 255, 0.2)", overflow: "hidden", backdropFilter: "blur(5px)" }}>
        <input
          className="form-control border-0 bg-transparent placeholder-white text-white shadow-none px-4"
          type="search"
          placeholder="Search movies & series..."
          aria-label="Search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setShowSuggestions(true);
          }}
          onFocus={() => {
            if (suggestions.length > 0) setShowSuggestions(true);
          }}
          style={{ width: isMobile ? "100%" : "220px", flex: isMobile ? 1 : "none", transition: "width 0.3s ease" }}
        />
        <button className="btn border-0 text-white d-flex align-items-center justify-content-center px-3" type="submit" style={{ backgroundColor: "rgba(231,184,22,0.15)" }}>
          {isMobile ? "Search" : (
             <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-search" viewBox="0 0 16 16">
               <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
             </svg>
          )}
        </button>
      </form>

      <AnimatePresence>
        {showSuggestions && suggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="position-absolute w-100 rounded shadow-lg overflow-hidden"
            style={{ 
              top: '100%', 
              left: 0, 
              zIndex: 1050, 
              backgroundColor: "rgba(15, 7, 22, 0.95)", 
              border: "1px solid rgba(231, 184, 22, 0.5)", 
              maxHeight: '400px', 
              overflowY: 'auto', 
              marginTop: '8px',
              backdropFilter: "blur(10px)"
            }}
          >
            {suggestions.map((s) => {
              const sTitle = getTitle(s);
              const sType = getMediaType(s);
              const sPoster = posterUrl(s.poster_path);
              const sYear = getReleaseYear(s);

              return (
                <div
                  key={`${sType}-${s.id}`}
                  className="p-3 border-bottom d-flex align-items-center"
                  style={{ 
                    cursor: 'pointer', 
                    color: 'white', 
                    borderColor: 'rgba(255,255,255,0.1) !important',
                    transition: "background-color 0.2s ease"
                  }}
                  onClick={() => {
                    setQuery(sTitle);
                    setShowSuggestions(false);
                    setShowMobileSearch(false);
                    navigate(`/details/${sType}/${s.id}`);
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(231, 184, 22, 0.2)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  {sPoster ? (
                    <img 
                      src={sPoster} 
                      alt={sTitle} 
                      className="rounded me-3 shadow-sm"
                      style={{ width: '40px', height: '60px', objectFit: 'cover' }} 
                    />
                  ) : (
                    <div 
                      className="rounded me-3 d-flex justify-content-center align-items-center"
                      style={{ width: '40px', height: '60px', background: "rgba(255,255,255,0.08)" }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" viewBox="0 0 16 16">
                        <path d="M0 1a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1zm4 0v6h8V1zm8 8H4v6h8zM1 1v2h2V1zm2 3H1v2h2zM1 7v2h2V7zm2 3H1v2h2zm-2 3v2h2v-2zM15 1h-2v2h2zm-2 3v2h2V4zm2 3h-2v2h2zm-2 3v2h2v-2zm2 3h-2v2h2z"/>
                      </svg>
                    </div>
                  )}
                  <div>
                    <div className="fw-bold" style={{ fontSize: '1rem' }}>{sTitle}</div>
                    <div className="small text-white-50 d-flex align-items-center mt-1">
                      <span className="badge me-2" style={{ backgroundColor: "#e7b816", color: "#000" }}>{sType === "tv" ? "Series" : "Movie"}</span>
                      {sYear}
                    </div>
                  </div>
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  return (
    <motion.div 
      className="container-fluid glass-nav mb-4 position-relative"
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      style={{ zIndex: 1040 }}
    >
      <div className="container">
        <div className="row">
          <div className="col-12 position-relative">
            <nav className="navbar navbar-expand-lg">
              <a
                href="/"
                className="navbar-brand d-flex align-items-center mb-0 me-md-auto link-body-emphasis text-decoration-none"
              >
                <div className="svg-container" style={{ filter: "drop-shadow(0 0 6px rgba(231,184,22,0.8))" }}>
                  <svg
                    className="bi bi-film"
                    xmlns="http://www.w3.org/2000/svg"
                    width="36"
                    height="30"
                    fill="var(--accent)"
                    viewBox="0 0 16 16"
                  >
                    <path d="M0 1a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1zm4 0v6h8V1zm8 8H4v6h8zM1 1v2h2V1zm2 3H1v2h2zM1 7v2h2V7zm2 3H1v2h2zm-2 3v2h2v-2zM15 1h-2v2h2zm-2 3v2h2V4zm2 3h-2v2h2zm-2 3v2h2v-2zm2 3h-2v2h2z" />
                  </svg>
                </div>
                <span className="fs-3 logo-name ms-2 text-gradient" style={{ fontWeight: '900', letterSpacing: '-0.5px' }}>ReelReviews</span>
              </a>
              
              <div className="d-flex align-items-center d-lg-none ms-auto me-3">
                <button 
                  className="btn btn-link text-white p-0" 
                  onClick={() => setShowMobileSearch(!showMobileSearch)}
                  aria-label="Toggle mobile search"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" className="bi bi-search" viewBox="0 0 16 16">
                    <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
                  </svg>
                </button>
              </div>

              <button
                className="navbar-toggler"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#navbarTogglerDemo02"
                aria-controls="navbarTogglerDemo02"
                aria-expanded="false"
                aria-label="Toggle navigation"
                style={{ backgroundColor: "#e7b816" }}
              >
                <span className="navbar-toggler-icon"></span>
              </button>

              <div className="collapse navbar-collapse" id="navbarTogglerDemo02">
                <ul className="nav nav-pills navbar-nav ms-auto mb-2 mt-4 mt-lg-0 mb-lg-0">
                  <li className="nav-item ms-1">
                    <a href="/" className={"nav-link " + props.home + " px-3"} aria-current="page">
                      Home
                    </a>
                  </li>
                  <li className="nav-item ms-1">
                    <a href="/movies" className={"nav-link " + props.movies + " px-3"}>
                      Movies
                    </a>
                  </li>
                  <li className="nav-item ms-1">
                    <a href="/series" className={"nav-link " + props.series + " px-3"}>
                      Series
                    </a>
                  </li>
                  <li className="nav-item ms-1">
                    <a href="/about" className={"nav-link " + props.about + " px-3"}>
                      About Us
                    </a>
                  </li>
                </ul>
                <div className="d-none d-lg-block ms-lg-2">
                  {renderSearchForm(false)}
                </div>
                <div className="d-flex align-items-center ms-lg-4 mt-3 mt-lg-0 pt-3 pt-lg-0">
                  {currentUser ? (
                    <div className="d-flex align-items-center bg-dark rounded-pill p-1 pe-3 shadow-sm border" style={{ borderColor: "var(--glass-border) !important", backgroundColor: "rgba(0,0,0,0.5) !important" }}>
                      <img 
                        src={currentUser.photoURL || "https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png"} 
                        alt={currentUser.displayName || "Profile"} 
                        width="36" 
                        height="36" 
                        className="rounded-circle me-3 border border-2 shadow-sm"
                        style={{ borderColor: "var(--accent)" }}
                        referrerPolicy="no-referrer"
                      />
                      <button className="btn btn-sm text-white fw-bold p-0 text-decoration-underline" style={{ fontSize: "0.9rem" }} onClick={logout}>Sign Out</button>
                    </div>
                  ) : (
                    <button className="btn btn-warning rounded-pill px-4 py-2 shadow fw-bold" onClick={loginWithGoogle}>Sign In</button>
                  )}
                </div>
              </div>
            </nav>
            
            <AnimatePresence>
              {showMobileSearch && (
                <motion.div 
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="d-lg-none overflow-visible position-absolute w-100 start-0"
                  style={{ top: '100%', zIndex: 1040 }}
                >
                  <div className="p-3 bg-dark rounded shadow-lg border border-warning" style={{ backgroundColor: "rgb(15, 7, 22) !important" }}>
                    {renderSearchForm(true)}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default NavBar;
